import { ReactionCounts, REACTIONS } from "@/src/models/post.model";
import ReactionGlyph from "@/src/components/posts/reactions/reaction-glyph";
import {
  deduplicateReactionsByUser,
  useGetReactions,
} from "@/src/services/api/posts/posts.hooks";
import { useTheme } from "@/src/styles/theme-provider";
import { CloseCircle } from "iconsax-react-nativejs";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  ActivityIndicator,
  FlatList,
  Image,
  Modal,
  Pressable,
  Text,
  View,
} from "react-native";
import UserAvatar from "@/src/components/ui-kit/user-avatar";

/**
 * How many rows the selected tab should hold before the modal stops pulling
 * pages on its own. Reactions are paged for the post as a whole and split into
 * tabs on the client, so the tab in front of the viewer can be nearly empty
 * while later pages hold nothing but rows for it — without this the list showed
 * "a few users" for a type the counter said had dozens, and no amount of
 * scrolling reached the rest. Matches the request page size, so a tab is topped
 * up one page at a time and stops as soon as it has a screenful; from there on
 * it is the viewer's scrolling that pulls pages, one per scroll.
 */
const MIN_ROWS_PER_TAB = 10;

type ReactorModalProps = Readonly<{
	// Optional because callers open this from whichever post is currently
	// selected, which can be nothing yet on first render.
	postId?: string;
	reactions: ReactionCounts;
	showModal: boolean;
	onClose: () => void;
}>;

const ReactorModal = ({ showModal, onClose, reactions, postId }: ReactorModalProps) => {
	const { themeVars } = useTheme();
	const {t} = useTranslation();

	const [selectedReaction, setSelectedReaction] = useState<string>("");

	useEffect(() => {
		if (showModal) {
			const firstActiveReaction =
				REACTIONS.find(r => reactions[r.id] > 0)?.id || REACTIONS[0].id;

			setSelectedReaction(firstActiveReaction);
		}
	}, [showModal, reactions]);

	const activeReactions = REACTIONS.filter(reaction => reactions[reaction.id] > 0);

	const { data, fetchNextPage, hasNextPage, isFetchingNextPage } =
		useGetReactions(postId ?? "");
	const rawReactions = data?.pages.flatMap((page) => page?.getreactions?.items ?? []) ?? [];
	// Defensive dedup: keep one row per userId (most-recent first, thanks to DESC order)
	const reactionsData = deduplicateReactionsByUser(rawReactions);

	// Filter reactions by selected type
	const filteredReactions = reactionsData.filter(item => item.type === selectedReaction);

	// `isFetchingNextPage` only flips after react-query re-renders, and
	// onEndReached fires several times within one scroll gesture, so the flag
	// alone let a single scroll queue a burst of requests. The ref closes that
	// window: it is set in the same tick the fetch starts.
	const isLoadingMoreRef = useRef(false);

	const loadMore = useCallback(() => {
		if (!hasNextPage || isFetchingNextPage || isLoadingMoreRef.current) return;
		isLoadingMoreRef.current = true;
		fetchNextPage().finally(() => {
			isLoadingMoreRef.current = false;
		});
	}, [hasNextPage, isFetchingNextPage, fetchNextPage]);

	// Top up the selected tab until it has enough rows to scroll, or the post has
	// no more pages. Runs on tab switch too, which is when a sparse tab appears.
	useEffect(() => {
		if (!showModal) return;
		if (filteredReactions.length >= MIN_ROWS_PER_TAB) return;
		loadMore();
	}, [showModal, selectedReaction, filteredReactions.length, loadMore]);

	return (
		<Modal visible={showModal} transparent animationType="fade">
			<View style={themeVars as any} className="flex-1">
				<View
					className="flex-1 items-center justify-center px-4"
					style={{ backgroundColor: 'var(--color-shadow-dark)' }}
				>
					<View
						className="w-full max-w-md bg-white rounded-2xl"
						style={{ height: "80%", maxHeight: 600 }}
					>
						{/* Header */}
						<View className="flex-row justify-between items-center gap-3 px-5 pt-5 pb-4 border-b border-gray-100">
							<Text className="text-lg font-semibold text-gray-900">{t("LABEL_REACTIONS")}</Text>
							<Pressable onPress={onClose} className="p-1.5 hover:bg-ash-3 hover:rounded-full">
								<CloseCircle size={20} color={'var(--color-ash-1)'} />
							</Pressable>
						</View>

						{/* Emoji Tabs */}
						{activeReactions.length > 0 ? (
							<>
								<View className="border-b border-gray-100">
									<View className="flex-row px-2">
										{activeReactions.map((reaction) => (
											<Pressable
												key={reaction.id}
												onPress={() => setSelectedReaction(reaction.id)}
												className={`flex-row gap-2 px-4 py-3 items-center hover:rounded-lg ${selectedReaction === reaction.id ? 'border-b-2 border-blue-500 hover:bg-primary-2' : 'hover:bg-ash-3'
													}`}
											>
												<ReactionGlyph id={reaction.id} size={24} />
												<Text
													className={`text-xs mt-1 ${selectedReaction === reaction.id
															? 'text-blue-500 font-semibold'
															: 'text-gray-500'
														}`}
												>
													{reactions[reaction.id]}
												</Text>
											</Pressable>
										))}
									</View>
								</View>

								{/* A FlatList, not a mapped ScrollView: the reactor list is
								    paged, so it needs an end-reached hook to pull the next page
								    as the viewer scrolls rather than stopping at page one. */}
								<FlatList
									className="flex-1"
									data={filteredReactions}
									keyExtractor={(reaction, index) => reaction.ItemId || `${index}`}
									contentContainerStyle={{ padding: 20, gap: 12 }}
									onEndReached={loadMore}
									onEndReachedThreshold={0.4}
									ListEmptyComponent={
										isFetchingNextPage ? null : (
											<Text className="text-gray-500 mt-2">
												{t("LABEL_NO_USERS_YET")}
											</Text>
										)
									}
									ListFooterComponent={
										isFetchingNextPage ? (
											<View className="py-3">
												<ActivityIndicator />
											</View>
										) : null
									}
									renderItem={({ item: reaction }) => {
										const validName =
											reaction.reactorName &&
											reaction.reactorName !== 'undefined undefined'
												? reaction.reactorName
												: undefined;
										const displayName = validName ?? 'Unknown User';
										const nameParts = validName?.split(' ') ?? [];
										return (
											<View className="flex-row items-center gap-3">
												{reaction.reactorProfileImageUrl ? (
													<Image
														source={{ uri: reaction.reactorProfileImageUrl }}
														className="w-10 h-10 rounded-full"
													/>
												) : (
													<UserAvatar
														firstName={nameParts[0]}
														lastName={nameParts.slice(1).join(' ') || undefined}
														size={40}
													/>
												)}
												<View className="flex-1">
													<Text className="text-gray-900 font-medium">
														{displayName}
													</Text>
												</View>
											</View>
										);
									}}
								/>
							</>
						) : (
							<View className="flex-1 items-center justify-center p-5">
								<Text className="text-gray-500">{t("LABEL_NO_REACTIONS_YET")}</Text>
							</View>
						)}
					</View>
				</View>
			</View>
		</Modal>
	);
};

export default ReactorModal;
